<script>
import DocumentRightWithAnalyst from '@/V2/documents/document-view/DocumentRightWithAnalyst.vue';
import DocumentRightNav from '@/V2/documents/document-view/DocumentRightNav.vue';
import DocumentDetails from '@/V2/documents/document-view/DocumentDetails.vue';
import DocumentViewToolButton from '@/V2/documents/document-view/DocumentViewToolButton.vue';
import { iButton, iButtonStyles, iButtonTypes } from '@inscribe/signal';
import { encodeQueryParams } from '@/utils/queryParams';
import { logError } from '@/utils/error';

export default {
  name: 'DocumentRightPanel',
  components: {
    DocumentDetails,
    DocumentRightNav,
    DocumentRightWithAnalyst,
    DocumentViewToolButton,
    iButton,
  },
  props: {
    document: {
      type: Object,
      default: null,
    },
    customerId: {
      type: String,
      default: null,
    },
    config: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      overlays: [],
      toggleRotated: false,
      encodeQueryParams,
      iButtonStyles,
      iButtonTypes,
      shouldShowDocumentDetails: false,
      documentDetailsLoadedOnce: false,
      navSelectedItem: 'analysis',
    };
  },
  watch: {
    config: {
      immediate: true,
      handler(config) {
        if (!config?.panel) {
          this.overlays = [];
          this.closeAllOverlays();
        }
      },
    },
  },
  methods: {
    onOpenOverlay(overlayComponent) {
      const overlayEl = overlayComponent.$el ?? overlayComponent;
      if (!overlayEl) {
        // Log a Sentry error so we can help narrow down specific browsers or
        // documents where this is happening.
        logError(
          new Error(
            `Expected Document Right Panel overlay element not found: ${overlayComponent}`,
          ),
        );
        return;
      }
      this.overlays.push(overlayEl);
      overlayEl.classList.add('overlay-item');
      setTimeout(() => {
        overlayEl.classList.add('open');
      });
      this.$refs.overlayContent.appendChild(overlayEl);
      setTimeout(() => {
        if (this.$refs?.overlayContent) {
          this.$refs.overlayContent.scrollTop = 0;
        }
        if (this.$refs?.overlay) {
          this.$refs.overlay.scrollTop = 0;
        }
      }, 100);

      setTimeout(() => {
        this.setOverlayScrollable();
      }, 300);
    },
    setOverlayScrollable() {
      const currentOverlay = this.overlays[this.overlays.length - 1];
      Array.from(this.$refs.overlayContent.children).forEach((child) => {
        child.style.overflow = child !== currentOverlay ? 'hidden' : 'auto';
        child.style.height = child !== currentOverlay ? '0px' : 'auto';
      });
    },
    closeAllOverlays() {
      for (let i = 0; i <= this.overlays.length; i++) {
        setTimeout(() => {
          this.closeLastOverlay();
        }, i * 100);
      }
    },
    closeLastOverlay() {
      if (this.overlays.length) {
        const lastLayer = this.overlays.pop();
        if (lastLayer) {
          lastLayer.classList?.remove('open');
          this.$emit('close-overlay', lastLayer);
        }
        this.setOverlayScrollable();
      }
      this.resetNavSelectedItem();
    },
    togglePanel() {
      this.$emit('toggle-panel', 'right');
      this.toggleRotated = !this.toggleRotated;
    },
    showSection(section) {
      this.shouldShowDocumentDetails = section === 'details';
      if (!this.documentDetailsLoadedOnce && section === 'details') {
        this.documentDetailsLoadedOnce = true;
      }
      this.closeAllOverlays();
    },
    resetNavSelectedItem() {
      this.navSelectedItem = 'analysis';
    },
    openSimilarDocuments() {
      if (this.$refs.documentRightWithAnalyst) {
        this.$refs.documentRightWithAnalyst.openTrustScoreDetails();
      }
    },
  },
};
</script>

<template>
  <div
    class="right-panel d-flex flex-column align-items-start"
    data-testid="document-right-panel"
  >
    <!-- Nav tabs -->
    <document-right-nav
      :document="document"
      :nav-selected-item="navSelectedItem"
      class="document-panel-nav"
      @show-section="showSection"
    ></document-right-nav>

    <document-view-tool-button
      id="open-toggle-right"
      tooltip-title="Toggle panel"
      :toggle-rotated="toggleRotated"
      icon="medium-layout-column-right-hide"
      @button-click="togglePanel()"
    />

    <!-- Content with Analyst -->
    <document-right-with-analyst
      ref="documentRightWithAnalyst"
      :class="{ visible: !shouldShowDocumentDetails }"
      class="document-right-panel-container"
      :document="document"
      :customer-id="customerId"
      :current-overlays="overlays"
      @open-overlay="onOpenOverlay"
    ></document-right-with-analyst>

    <document-details
      v-if="shouldShowDocumentDetails || documentDetailsLoadedOnce"
      :class="{ visible: shouldShowDocumentDetails }"
      class="document-right-panel-container"
      ref="documentDetails"
      data-testid="document-details"
      :document="document"
    ></document-details>

    <!-- overlay -->
    <div
      ref="overlay"
      :class="{ 'has-open-overlays': overlays.length }"
      class="overlays-container"
    >
      <i-button
        class="close-icon back"
        :left-icon="'medium-arrows-left'"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.GHOST"
        data-testid="overlay-back-button"
        @button-click="closeLastOverlay"
      ></i-button>
      <i-button
        :class="{ visible: overlays.length > 1 }"
        class="close-icon all"
        :left-icon="'medium-delete-1'"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.GHOST"
        @button-click="closeAllOverlays()"
      ></i-button>
      <div ref="overlayContent" class="content i-mt-13"></div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.right-panel {
  background-color: $light-neutral-default;
  position: relative;
  width: 100%;

  .document-right-panel-container {
    transition: opacity 150ms ease-out;
    opacity: 0;
    height: 0;

    &.visible {
      opacity: 1;
      height: auto;
    }
  }

  .document-panel-nav {
    width: 100%;
  }

  .document-view-tool-button {
    position: absolute;
    bottom: 16px;
    left: -58px;
    z-index: 100;
  }

  .i-accordion {
    visibility: visible;
    transition: visibility 0.3s ease-out;
  }

  .overlays-container {
    position: absolute;
    background-color: white;
    top: 40px;
    padding-bottom: 56px;
    width: 100%;
    bottom: 0;
    right: -100%;
    transition: right 0.3s ease;

    &.has-open-overlays {
      right: 0;

      .close-icon {
        display: flex;
        z-index: 10;

        &.all {
          opacity: 0;
          transition: all 300ms ease-in-out;
          pointer-events: none;

          &.visible {
            opacity: 1;
            pointer-events: all;
            transition: all 900ms ease-in-out;
          }
        }
      }
    }

    .content {
      position: relative;
      width: 460px;
      height: 100%;
      overflow-x: hidden;

      :deep(.overlay-item) {
        background-color: $light-neutral-default;
        position: absolute;
        top: 0;
        min-height: 100%;
        right: -460px;
        width: 460px;
        transition: right 0.3s ease;

        > .i-accordion {
          visibility: hidden;
        }

        &.open {
          right: 0;

          > .i-accordion {
            visibility: visibile;
          }
        }
      }
    }

    .close-icon {
      display: none;
      position: absolute;
      top: 24px;

      &.back {
        left: 24px;
      }

      &.all {
        right: 24px;
      }
    }
  }
}
</style>
